import { useState, useEffect, useRef } from 'react';

/**
 * Anima un número de 0 hasta `target`.
 * duration  — duración fija en ms (se ignora si se pasa msPerUnit)
 * msPerUnit — ms por unidad: la duración escala con el valor (ej: 150ms × 15 = 2250ms)
 * minDuration / maxDuration — límites cuando se usa msPerUnit
 * delay     — retardo inicial en ms antes de arrancar
 */
export default function useCountUp(target, {
  duration,
  msPerUnit,
  minDuration = 1200,
  maxDuration = 3500,
  delay = 0,
} = {}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target === null || target === undefined) return;

    const resolvedDuration = msPerUnit !== undefined
      ? Math.min(maxDuration, Math.max(minDuration, target * msPerUnit))
      : (duration ?? 1200);

    let startTime = null;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / resolvedDuration, 1);
      // ease-out cuadrática: más suave que cúbica, sensación de conteo más lineal
      const eased = 1 - Math.pow(1 - progress, 2);
      setValue(Math.round(target * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    const timeoutId = setTimeout(() => {
      rafRef.current = requestAnimationFrame(tick);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, msPerUnit, minDuration, maxDuration, delay]);

  return target === null || target === undefined ? null : value;
}
